import React, { useState, useMemo, useEffect } from "react";
import { FormControl, InputLabel, Select, MenuItem, Grid, Typography, TextField, InputAdornment, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const SearchableDropdown = ({
    dropDownList,
    value,
    handleChange,
    disabled,
    label,
    size,
    name,
    placeholder,
    valueKey,
    dropDownValueKey,
    showAllOption = true,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [open, setOpen] = useState(false);

    const filteredList = useMemo(() => {
        if (!dropDownList || !searchTerm) return dropDownList;

        const filtered = !searchTerm
            ? dropDownList
            : dropDownList.filter(value => {
                  if (dropDownValueKey) {
                      return value?.[dropDownValueKey]?.toLowerCase().includes(searchTerm.toLowerCase());
                  } else {
                      return value?.toLowerCase().includes(searchTerm.toLowerCase());
                  }
              });

        if (dropDownValueKey) {
            return filtered.sort((a, b) => (a?.[dropDownValueKey] || "").localeCompare(b?.[dropDownValueKey] || ""));
        }

        return filtered.sort((a, b) => (a || "").localeCompare(b || ""));
    }, [dropDownList, searchTerm]);

    const handleChangeDropDown = e => {
        handleChange(e);
        setOpen(false);
    };

    const handleSearchChange = e => {
        setSearchTerm(e.target.value);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSearchTerm(""); // Clear search when closing
    };

    return (
        <FormControl size="small" fullWidth>
            <InputLabel id={label}>{label}</InputLabel>
            <Select
                disabled={disabled}
                name={name}
                labelId={label}
                id={label}
                value={value || (showAllOption ? "All" : "")}
                onChange={handleChangeDropDown}
                onOpen={handleOpen}
                onClose={handleClose}
                open={open}
                label={label}
                size={size}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                            maxHeight: 300,
                        },
                    },
                }}
            >
                {/* Search Field */}
                <Box sx={{ px: 2, py: 1, position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                    <TextField
                        size={size}
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onClick={e => e.stopPropagation()} // Prevent menu from closing
                        onKeyDown={e => e.stopPropagation()} // Prevent menu navigation
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize={size} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#f5f5f5",
                            },
                        }}
                    />
                </Box>

                {/* All Option */}

                {showAllOption && !searchTerm && (
                    <MenuItem value="All">
                        <Grid
                            container
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <Grid item md={6} display={"flex"} flexDirection={"column"}>
                                <Typography sx={{ fontSize: "0.9rem", fontWeight: "400" }}>All</Typography>
                            </Grid>
                        </Grid>
                    </MenuItem>
                )}

                {filteredList && filteredList.length > 0 ? (
                    filteredList.map((value, index) => (
                        <MenuItem key={index} value={valueKey ? value[valueKey] : value}>
                            <Grid
                                container
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Grid item md={6} display={"flex"} flexDirection={"column"}>
                                    <Typography sx={{ fontSize: "0.9rem", fontWeight: "400" }}>
                                        {dropDownValueKey ? value[dropDownValueKey] : value}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </MenuItem>
                    ))
                ) : searchTerm ? (
                    <MenuItem disabled>
                        <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>Not found</Typography>
                    </MenuItem>
                ) : null}
            </Select>
        </FormControl>
    );
};

export default SearchableDropdown;
